/**
 * useCodes — 공통 코드 그룹 캐시 훅 (Zustand store 기반).
 *
 * 【역할】
 * - cm_code 테이블의 그룹별 코드 목록을 앱 전역에 1회 로드 후 재사용.
 * - Select/Tag/Column render 등에서 code → name 조회, options 제공.
 *
 * 【주요 기능】
 * - loadCodes(groups): 미로드 그룹만 병렬 조회 (이미 캐시된 그룹 skip)
 * - getCodeName(group, code): 코드 → 표시명 (미존재 시 code 반환)
 * - getOptions(group): `{label, value}[]` — antd Select 에 바로 바인딩
 *
 * 【내부 상태 / 캐시 구조】
 * - useCodesStore: { cache: Record<group, CodeItem[]>, loading: Record<group, boolean> }
 * - LOCALE_CHANGE_EVENT 수신 시 clearAll → 다음 loadCodes 가 새 locale 로 재조회.
 *
 * 【사용 예시】
 * ```tsx
 * const { loadCodes, getOptions, getCodeName } = useCodes()
 * useEffect(() => { void loadCodes(['USER_STATUS', 'DEPT']) }, [])
 * <Select options={getOptions('USER_STATUS')} />
 * ```
 *
 * 【왜 이 패턴?】
 * - vue-spring-fw 원본 composable 포팅 — 공통 코드는 그룹 단위 일괄 로드가 관용.
 * - Zustand store 로 컴포넌트 간 캐시 공유 (여러 페이지에서 동일 그룹 요청 시 1회만 HTTP).
 */
import { useCallback } from 'react'
import { create } from 'zustand'
import http from './api/axios'
import { LOCALE_CHANGE_EVENT } from './useLocale'

/** 코드 단건 — extra 는 원본 레코드 전체 (정렬/색상/플래그 등). */
export interface CodeItem {
  code: string
  name: string
  extra?: Record<string, unknown>
}

/** Zustand store 상태 — 그룹별 로드 상태/데이터 분리 보관. */
interface CodesState {
  cache: Record<string, CodeItem[]>
  loading: Record<string, boolean>
  setGroup: (group: string, items: CodeItem[]) => void
  setLoading: (group: string, v: boolean) => void
  clearAll: () => void
}

const useCodesStore = create<CodesState>((set) => ({
  cache: {},
  loading: {},
  setGroup: (group, items) => set((s) => ({ cache: { ...s.cache, [group]: items } })),
  setLoading: (group, v) => set((s) => ({ loading: { ...s.loading, [group]: v } })),
  clearAll: () => set({ cache: {}, loading: {} })
}))

// locale 변경 시 전역 캐시 무효화
if (typeof window !== 'undefined') {
  window.addEventListener(LOCALE_CHANGE_EVENT, () => {
    useCodesStore.getState().clearAll()
  })
}

/**
 * useCodes — 코드 캐시 구독 + 조회/로더.
 * @returns loadCodes/getCodeName/getOptions/cache 조합.
 */
export function useCodes() {
  const cache = useCodesStore((s) => s.cache)
  const setGroup = useCodesStore((s) => s.setGroup)
  const setLoading = useCodesStore((s) => s.setLoading)

  /**
   * 여러 코드 그룹 병렬 로드 — 이미 캐시된 그룹은 skip.
   * 실패한 그룹은 빈 배열로 캐시 (반복 호출 폭주 방지).
   */
  const loadCodes = useCallback(
    async (groups: string[]): Promise<void> => {
      const toLoad = groups.filter((g) => !useCodesStore.getState().cache[g])
      if (!toLoad.length) return
      toLoad.forEach((g) => setLoading(g, true))
      try {
        const results = await Promise.all(
          toLoad.map((g) =>
            http
              .get(`/codes/${encodeURIComponent(g)}`)
              .then((res) => res.data?.data ?? res.data ?? [])
              .catch(() => [])
          )
        )
        toLoad.forEach((g, idx) => {
          const raw = results[idx]
          const rows: Record<string, unknown>[] = Array.isArray(raw) ? raw : []
          const items: CodeItem[] = rows.map((row) => ({
            code: String(row.code ?? ''),
            name: String(row.codeName ?? row.name ?? row.code ?? ''),
            extra: row
          }))
          setGroup(g, items)
        })
      } catch {
        toLoad.forEach((g) => setGroup(g, []))
      } finally {
        toLoad.forEach((g) => setLoading(g, false))
      }
    },
    [setGroup, setLoading]
  )

  /**
   * 코드 → 표시명 변환 — 미존재 시 code 를 그대로 반환 (fail-safe).
   */
  const getCodeName = useCallback(
    (group: string, code: string): string => {
      const list = cache[group] ?? []
      return list.find((c) => c.code === code)?.name ?? code
    },
    [cache]
  )

  /**
   * antd Select 용 options 반환 — 캐시 미로드 시 빈 배열.
   */
  const getOptions = useCallback(
    (group: string): { label: string; value: string }[] => {
      const list = cache[group] ?? []
      return list.map((c) => ({ label: c.name, value: c.code }))
    },
    [cache]
  )

  return { loadCodes, getCodeName, getOptions, cache }
}
